// Program-performance metrics API.
//
// These hooks fetch, cache and nothing else. No rate, percentage, median or
// verdict is computed here -- every figure arrives decided by
// services/metrics.py, and a metric the server withheld simply has no numbers
// in its payload. Recomputing anything client-side would reintroduce the
// suppression decision in a place that is not the security boundary.

import { useQuery } from '@tanstack/react-query';

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('wmf_token')}`,
});

const request = async (path, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    // Absent is not the same as empty: an omitted `sequence` means "all
    // semesters", while `sequence=` would be rejected as a non-integer.
    if (value !== null && value !== undefined && value !== '') {
      query.append(key, value);
    }
  });

  const suffix = query.toString() ? `?${query}` : '';
  const response = await fetch(`${API_URL}/metrics${path}${suffix}`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.msg || `Request failed (${response.status})`);
  }
  return response.json();
};

// The registry: labels, scales, value names and quarantine reasons. Lets a view
// render an instrument it has never heard of, which is the point -- three more
// assessment protocols are coming and none of them should need a new component.
export const useInstrumentRegistry = () =>
  useQuery({
    queryKey: ['metrics', 'instruments'],
    queryFn: () => request('/instruments'),
    staleTime: 60 * 60 * 1000, // definitions, not data
  });

export const useMetricsOverview = (filters = {}) =>
  useQuery({
    queryKey: ['metrics', 'overview', filters],
    queryFn: () => request('/overview', filters),
  });

export const useInstrumentMetrics = (instrumentKey, filters = {}) =>
  useQuery({
    queryKey: ['metrics', 'instrument', instrumentKey, filters],
    queryFn: () => request(`/${instrumentKey}`, filters),
    enabled: Boolean(instrumentKey),
  });

export const useCrossInstrument = ({ comparison, minItems } = {}) =>
  useQuery({
    queryKey: ['metrics', 'cross-instrument', comparison, minItems],
    queryFn: () => request('/cross-instrument', { comparison, minItems }),
  });

// Display helpers. Formatting only -- each takes a number the server already
// decided and turns it into text.

export const asPercent = (rate) =>
  rate === null || rate === undefined ? null : `${Math.round(rate * 100)}%`;

export const asSigned = (value, digits = 1) => {
  if (value === null || value === undefined) return null;
  const rounded = Number(value).toFixed(digits);
  return Number(rounded) > 0 ? `+${rounded}` : `${rounded}`;
};

// p-values near zero are more honest as a bound than as 0.000.
export const asPValue = (p) => {
  if (p === null || p === undefined) return null;
  if (p < 0.001) return 'p < 0.001';
  return `p = ${p.toFixed(3)}`;
};

export const VERDICT_LABELS = {
  reportable: 'Reportable',
  quarantined: 'Withheld: data quality',
  suppressed: 'Withheld: too few students',
  inadequate: 'Withheld: sample too small',
  low_coverage: 'Withheld: too much missing data',
};

export const VERDICT_STYLES = {
  reportable: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  quarantined: 'bg-red-50 text-red-700 border-red-200',
  suppressed: 'bg-slate-100 text-slate-600 border-slate-200',
  inadequate: 'bg-amber-50 text-amber-700 border-amber-200',
  low_coverage: 'bg-amber-50 text-amber-700 border-amber-200',
};
